import React, { createContext, useContext, useState, useEffect } from 'react';
import { ConfigProvider, theme as antdTheme } from 'antd';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

// Define theme configurations - Cox Brand (light + dark)
export const themes = {
  'light-cox-brand': {
    name: 'Cox Brand',
    mode: 'light',
    // Cox brand palette
    //   primary  #0c80df  — vivid Cox blue
    //   pressed  #06477c  — deeper blue used for hover/pressed states
    primary: '#0c80df',
    secondary: '#06477c',
    background: '#f5f8fc',
    backgroundSecondary: '#e8eef6',
    sidebarGradient: 'linear-gradient(180deg, #f5f8fc 0%, #e1e9f3 100%)',
    logoGradient: 'linear-gradient(135deg, #06477c 0%, #0c80df 100%)',
    menuHover: 'linear-gradient(135deg, #e1ecf7 0%, #cfdef0 100%)',
    menuSelected: 'linear-gradient(135deg, #06477c 0%, #0c80df 100%)',
    border: '#cfd9e6',
    textPrimary: '#0b1f33',
    textSecondary: '#3a5670',
    textIcon: '#5d7894',
    // Aliases — many components read `--theme-text`, `--theme-hoverBg`, etc.
    text: '#0b1f33',
    textHeading: '#06477c',
    hoverBg: '#eef3fa',
    selectedBg: '#dceaf7',
    bgSecondary: '#e8eef6',
    shadow: 'rgba(12, 128, 223, 0.12)',
    shadowStrong: 'rgba(6, 71, 124, 0.25)',
    contentBg: '#ffffff',
    headerBg: '#ffffff',
    // Surface tokens used by cards, chat bubbles, page headers etc.
    cardBg: '#ffffff',
    cardBgMuted: '#f7f9fc',
    cardBorder: '#e3eaf3',
    chatAgentBg: '#ffffff',
    chatAgentBorder: '#e3eaf3',
  },
  'dark-cox-brand': {
    name: 'Cox Brand - Dark',
    mode: 'dark',
    primary: '#0c80df',
    secondary: '#06477c',
    background: '#0d1822',
    backgroundSecondary: '#15212e',
    sidebarGradient: 'linear-gradient(180deg, #0d1822 0%, #15212e 100%)',
    logoGradient: 'linear-gradient(135deg, #06477c 0%, #0c80df 100%)',
    menuHover: 'linear-gradient(135deg, #1a2a3a 0%, #233447 100%)',
    menuSelected: 'linear-gradient(135deg, #06477c 0%, #0c80df 100%)',
    border: '#243348',
    textPrimary: '#eef3fa',
    textSecondary: '#bfcedf',
    textIcon: '#8aa3c1',
    text: '#eef3fa',
    textHeading: '#ffffff',
    hoverBg: '#1a2a3a',
    selectedBg: '#1f3047',
    bgSecondary: '#15212e',
    shadow: 'rgba(12, 128, 223, 0.3)',
    shadowStrong: 'rgba(6, 71, 124, 0.55)',
    contentBg: '#10202e',
    headerBg: '#0d1822',
    cardBg: '#162435',
    cardBgMuted: '#1a2a3e',
    cardBorder: '#243348',
    chatAgentBg: '#162435',
    chatAgentBorder: '#243348',
  },
};

export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState(() => {
    // Migrate legacy theme keys (Silver & Gold / Dark Silver were removed
    // when the Cox Brand palette became the canonical theme).
    const legacyMap = {
      'light-silver-gold': 'light-cox-brand',
      'dark-silver': 'dark-cox-brand',
    };
    const savedTheme = localStorage.getItem('app-theme');
    if (savedTheme && legacyMap[savedTheme]) {
      localStorage.setItem('app-theme', legacyMap[savedTheme]);
      return legacyMap[savedTheme];
    }
    return savedTheme && themes[savedTheme] ? savedTheme : 'light-cox-brand';
  });

  const themeConfig = themes[currentTheme];

  // Apply CSS variables whenever theme changes
  useEffect(() => {
    const root = document.documentElement;
    Object.entries(themeConfig).forEach(([key, value]) => {
      if (key !== 'name' && key !== 'mode') {
        root.style.setProperty(`--theme-${key}`, value);
      }
    });
    // Expose mode as a data attribute so CSS can target dark-mode overrides
    // (e.g. patching components that hard-code a white background inline).
    root.setAttribute('data-theme', themeConfig.mode);
  }, [themeConfig]);

  // Save theme preference to localStorage
  const changeTheme = (themeName) => {
    if (themes[themeName]) {
      setCurrentTheme(themeName);
      localStorage.setItem('app-theme', themeName);
    }
  };

  // Ant Design theme configuration
  const antdThemeConfig = {
    algorithm: themeConfig.mode === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: {
      colorPrimary: themeConfig.primary,
      colorInfo: themeConfig.primary,
      colorSuccess: '#10b981',
      borderRadius: 6,
      colorBgContainer: themeConfig.contentBg,
      colorBgElevated: themeConfig.contentBg,
    },
  };

  const value = {
    currentTheme,
    themeConfig,
    themes,
    changeTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      <ConfigProvider theme={antdThemeConfig}>
        {children}
      </ConfigProvider>
    </ThemeContext.Provider>
  );
};
